import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import type { ConversationCall, ConversationCallStatus } from "@/lib/chat/types";

export type VideoCallSession = {
  callId: string;
  conversationId: string;
  roomUrl: string;
  token: string;
};

type CallRow = {
  id: string;
  conversation_id: string;
  created_by: string;
  daily_room_name: string;
  daily_room_url: string;
  status: ConversationCallStatus;
  created_at: string;
};

function mapCall(row: CallRow): ConversationCall {
  return {
    id: row.id,
    conversationId: row.conversation_id,
    createdBy: row.created_by,
    dailyRoomName: row.daily_room_name,
    dailyRoomUrl: row.daily_room_url,
    status: row.status,
    createdAt: row.created_at,
  };
}

async function accessToken() {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) {
    throw new Error("A videóhíváshoz be kell jelentkezni.");
  }
  return data.session.access_token;
}

async function videoCallRequest(body: Record<string, string>) {
  const token = await accessToken();
  const response = await fetch("/api/chat/video-call", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => null)) as
    | (VideoCallSession & { error?: string })
    | { error?: string }
    | null;
  if (!response.ok) {
    throw new Error(payload?.error || "A videóhívás nem indítható.");
  }
  return payload as VideoCallSession;
}

export async function startVideoCall(conversationId: string) {
  return videoCallRequest({ action: "start", conversationId });
}

export async function joinVideoCall(input: {
  conversationId: string;
  callId: string;
}) {
  return videoCallRequest({
    action: "join",
    conversationId: input.conversationId,
    callId: input.callId,
  });
}

export async function endVideoCall(input: {
  conversationId: string;
  callId: string;
}) {
  return videoCallRequest({
    action: "end",
    conversationId: input.conversationId,
    callId: input.callId,
  });
}

export async function listLiveConversationCalls(): Promise<ConversationCall[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("conversation_calls")
    .select(
      "id, conversation_id, created_by, daily_room_name, daily_room_url, status, created_at",
    )
    .in("status", ["ringing", "active"])
    .order("created_at", { ascending: false });
  if (error) {
    throw error;
  }
  return ((data ?? []) as CallRow[]).map(mapCall);
}
